import type { StringKey } from './strings';
import type { Ride } from './types';

export const TIP_CHOICES = [0, 1000, 2000, 5000] as const;


type PayRide = Pick<Ride, 'farePaise' | 'discountPaise' | 'riderPaysPaise' | 'tipPaise' | 'paymentMethod' | 'paymentStatus'>;

export type PayView = {
  /** what the money card says first */
  headKey: StringKey;
  /** the line over the tip chips: add a tip / hand the tip over in cash (fare already paid) / tip saved */
  tipLabelKey: StringKey;
  /** button wording */
  buttonKey: StringKey;
  farePaise: number;
  discountPaise: number;
  /** the tip shown: the saved one, else the one being picked */
  tipPaise: number;
  /** trip (fare − reward) + tip: everything the rider hands over */
  totalPaise: number;
  /** cash already confirmed by the driver */
  paid: boolean;
  /** the tip can still be changed (not saved yet) */
  canTip: boolean;
};

/**
 * The pay-and-rate card for a completed ride, by payment state (docs/API.md "Payment (after the trip)"):
 * cash PENDING → "Pay cash to Ravi"; PAID (the driver confirmed the cash, usually as the trip ends) → "Cash paid to
 * Ravi", and a tip picked now is handed over in cash too; online is not offered yet, so a FAILED/PENDING online
 * payment falls back to cash. Tips: whole rupees, once, after the trip (`POST …/tip`).
 */
export function payView(r: PayRide, pickedTipPaise: number): PayView {
  const saved = r.tipPaise > 0;
  const tip = saved ? r.tipPaise : Math.max(0, pickedTipPaise);
  const trip = r.riderPaysPaise ?? r.farePaise - (r.discountPaise ?? 0);
  const total = trip + tip;
  const paid = r.paymentStatus === 'PAID';
  const tipLabelKey: StringKey = saved ? 'done.tipped' : paid && tip > 0 ? 'done.handTip' : 'done.addTip';
  const base = { farePaise: r.farePaise, discountPaise: r.discountPaise ?? 0, tipPaise: tip, totalPaise: total, paid, canTip: !saved, tipLabelKey };
  if (r.paymentStatus === 'REFUNDED') return { ...base, headKey: 'done.refunded', buttonKey: 'done.btn' };
  if (paid) return { ...base, headKey: 'done.paidCash', buttonKey: 'done.btnPaid' };
  if (r.paymentMethod === 'ONLINE' && r.paymentStatus === 'FAILED') return { ...base, headKey: 'done.onlineFailed', buttonKey: 'done.btnPaid' };
  return { ...base, headKey: 'done.payCash', buttonKey: 'done.btnPaid' };
}
